import {
  getModalManager,
  type ModalShowConfig,
} from "@UI/ModalManager/index.js";
import {
  createList,
  type ListAPI,
  type ListItemProps,
} from "@UI/List/index.js";
import type { SketchSummary } from "@/types/project-types";
import "./SketchesBrowser.scss";

export interface SketchesBrowserConfig {
  readonly sketches: readonly SketchSummary[];
  readonly selectedSketchId?: string | null;
  readonly anchor?: ModalShowConfig["anchor"];
  readonly width?: number | string;
  readonly offset?: { x: number; y: number };
  readonly onSelect?: (sketchId: string) => void;
  readonly onAddNew?: () => void;
  readonly onSearchChange?: (query: string) => void;
}

export interface SketchesBrowserAPI {
  readonly getElement: () => HTMLElement;
  readonly show: () => void;
  readonly hide: () => void;
  readonly destroy: () => void;
}

export const createSketchesBrowser = (
  config: SketchesBrowserConfig
): SketchesBrowserAPI => {
  const {
    sketches,
    selectedSketchId = null,
    width = 510,
    anchor = "bottom-center",
    offset = { x: 0, y: 100 },
    onSelect,
    onAddNew,
    onSearchChange,
  } = config;

  const modal = getModalManager();

  const container = document.createElement("div");
  container.className = "sketches-browser";

  const listConfig: any = {
    items: sketches,
    selectedId: selectedSketchId,
    mapItem: (sketch: SketchSummary): ListItemProps => ({
      id: sketch.id,
      leftIcon: "modes_sketch",
      primaryLabel: sketch.title ?? "Untitled Sketch",
    }),
    onSelect: (id: string) => {
      onSelect?.(id);
      modal.hide();
    },
  };

  if (onSearchChange || onAddNew) {
    listConfig.header = {
      searchable: true,
      ...(onSearchChange && { onSearchChange }),
      ...(onAddNew && { onAddNew }),
    } satisfies import("@UI/List/index.js").ListHeaderConfig;
  }

  const list: ListAPI = createList<SketchSummary>(listConfig);

  container.appendChild(list.getElement());

  const show = (): void => {
    const cfg: ModalShowConfig = {
      content: container,
      anchor,
      offset,
      width,
      closeOnBackdrop: true,
    };
    modal.show(cfg);
    requestAnimationFrame(() => list.focusFirst());
  };

  const hide = (): void => {
    if (modal.getCurrentContent() === container) modal.hide();
  };

  const destroy = (): void => {
    hide();
    list.destroy();
    container.remove();
  };

  return { getElement: () => container, show, hide, destroy };
};
